// Report / Search view — searchable list of all DV tags with filters.
// Extracted from /app/frontend/src/pages/DiamondVerification.jsx.

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ArrowLeft, Search, Filter, X, RotateCcw } from 'lucide-react';
import { API, token, COLORS, CLARITIES, LABS } from './constants';
import { TagCard } from './TagCard';
import { TagDetail } from './TagDetail';

export const ReportView = ({ onClose }) => {
  const [q,           setQ]           = useState('');
  const [filterColor, setFilterColor] = useState('');
  const [filterClr,   setFilterClr]   = useState('');
  const [filterLab,   setFilterLab]   = useState('');
  const [results,     setResults]     = useState([]);
  const [selected,    setSelected]    = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [showFilter,  setShowFilter]  = useState(false);

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (filterColor) params.set('color', filterColor);
      if (filterClr)   params.set('clarity', filterClr);
      if (filterLab)   params.set('lab', filterLab);
      const res = await axios.get(`${API}/api/diamond-verification/search?${params}`,
        { headers: { Authorization: `Bearer ${token()}` } });
      setResults(res.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [q, filterColor, filterClr, filterLab]);

  useEffect(() => { search(); }, [search]);

  if (selected) return <TagDetail tag={selected} onClose={() => setSelected(null)} />;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-emerald-700 text-white px-4 py-3 flex items-center gap-3 shrink-0">
        <button onClick={onClose} className="p-2 rounded-full hover:bg-emerald-600">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <p className="flex-1 font-bold text-base">Search & Report</p>
      </div>

      {/* Search bar */}
      <div className="px-4 py-3 bg-white border-b border-gray-100 flex gap-2">
        <div className="flex-1 flex items-center gap-2 bg-gray-100 rounded-xl px-3 py-2">
          <Search className="w-4 h-4 text-gray-400 shrink-0" />
          <input
            type="text"
            inputMode="numeric"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search by Tag Number…"
            className="flex-1 bg-transparent text-sm text-gray-900 outline-none"
            data-testid="report-search-input"
          />
          {q && <button onClick={() => setQ('')}><X className="w-4 h-4 text-gray-400" /></button>}
        </div>
        <button
          onClick={() => setShowFilter(f => !f)}
          className={`p-2.5 rounded-xl border-2 ${showFilter || filterColor || filterClr || filterLab ? 'border-emerald-600 text-emerald-600' : 'border-gray-200 text-gray-400'}`}
        >
          <Filter className="w-4 h-4" />
        </button>
      </div>

      {/* Filter panel */}
      {showFilter && (
        <div className="bg-white border-b border-gray-100 px-4 py-3">
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div>
              <p className="text-[10px] text-gray-400 font-semibold uppercase mb-1">Colour</p>
              <select
                value={filterColor}
                onChange={e => setFilterColor(e.target.value)}
                className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5"
              >
                <option value="">All</option>
                {COLORS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <p className="text-[10px] text-gray-400 font-semibold uppercase mb-1">Clarity</p>
              <select
                value={filterClr}
                onChange={e => setFilterClr(e.target.value)}
                className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5"
              >
                <option value="">All</option>
                {CLARITIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <p className="text-[10px] text-gray-400 font-semibold uppercase mb-1">Lab</p>
              <select
                value={filterLab}
                onChange={e => setFilterLab(e.target.value)}
                className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5"
              >
                <option value="">All</option>
                {LABS.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
          </div>
          {(filterColor || filterClr || filterLab) && (
            <button
              onClick={() => { setFilterColor(''); setFilterClr(''); setFilterLab(''); }}
              className="text-xs text-red-500 font-semibold"
            >✕ Clear filters</button>
          )}
        </div>
      )}

      {/* Results */}
      <div className="flex-1 overflow-y-auto px-4 pt-3">
        {loading ? (
          <div className="text-center py-12 text-gray-400">
            <RotateCcw className="w-8 h-8 mx-auto mb-2 animate-spin opacity-50" />
          </div>
        ) : results.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Search className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No tags found</p>
          </div>
        ) : (
          results.map(tag => (
            <TagCard key={tag.id} tag={tag} onClick={() => setSelected(tag)} />
          ))
        )}
      </div>
    </div>
  );
};
